'use client';

import Link from 'next/link';
import CodePane from './CodePane';
import { jumpTo } from './TocRail';
import { docsPath, API_BASE } from './data/nav';
import { API } from './data/api';
import { roadmapItem } from '@/lib/roadmap';
import { methodChipStyle } from './data/highlight';

/**
 * API reference page: header with BASE/AUTH chips, method-colored jump
 * chips (same motion grammar as the compare tables — smooth scroll, −80px,
 * reduced-motion gated), then per-endpoint two-column blocks with a sticky
 * REQUEST/RESPONSE pane column. Prose-only blocks (no method) carry
 * mechanism notes.
 */
export default function ApiRefPage({ pageId }: { pageId: string }) {
  const api = API[pageId];
  const withMethod = api.blocks.filter((b) => b.method);

  return (
    <div className="mt-[26px] max-w-[1140px]">
      <h1 className="m-0 font-onest text-[31px] font-extrabold leading-[1.12] tracking-[-0.02em]">
        {api.label}
      </h1>
      <div className="mt-3.5 flex flex-wrap items-center gap-2">
        <span className="rounded-md border border-line-2 bg-surface-2 px-2.5 py-1 font-jetbrains text-[11px] text-muted">
          <span className="text-dim">BASE&nbsp;&nbsp;</span>
          {API_BASE}
        </span>
        <Link
          href={docsPath('api-auth')}
          className="rounded-md border border-line-2 bg-surface px-2.5 py-1 font-jetbrains text-[10px] tracking-[0.06em] text-muted transition-colors hover:border-brand-600 hover:text-brand-700"
        >
          AUTH: BEARER → AUTHENTICATION
        </Link>
      </div>
      <p className="mt-4 max-w-[640px] text-[15px] leading-[1.65] text-muted [text-wrap:pretty]">
        {api.lead}
      </p>

      {withMethod.length > 1 && (
        <div className="mt-5 flex flex-wrap gap-[7px]">
          {withMethod.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => jumpTo(b.id)}
              className="inline-flex items-center gap-[7px] rounded-full border border-line-3 bg-surface px-[11px] py-[5px] font-jetbrains text-[10.5px] text-muted transition-colors hover:border-brand-600 hover:text-ink"
            >
              <span
                className="font-semibold"
                style={{ color: methodChipStyle(b.method).color }}
              >
                {b.method}
              </span>
              {b.path}
            </button>
          ))}
        </div>
      )}

      {api.blocks.map((b) => (
        <section
          key={b.id}
          id={b.id}
          data-toc-id={b.id}
          className="mt-[34px] grid grid-cols-[minmax(0,1fr)_minmax(320px,42%)] items-start gap-[34px] border-t border-line-2 pt-[30px] max-[999px]:grid-cols-1"
        >
          <div className="min-w-0">
            {b.method && (
              <div className="flex flex-wrap items-center gap-[9px]">
                <span
                  className="rounded-md border px-2 py-[3px] font-jetbrains text-[10px] font-semibold tracking-[0.05em]"
                  style={methodChipStyle(b.method)}
                >
                  {b.method}
                </span>
                <code className="font-jetbrains text-[13.5px] text-ink">{b.path}</code>
              </div>
            )}
            <h3 className="mb-0 mt-3 font-onest text-[17.5px] font-bold tracking-[-0.01em]">
              {b.t}
            </h3>
            <p className="mt-2 text-sm leading-[1.65] text-muted [text-wrap:pretty]">{b.d}</p>

            {b.params && (
              <div className="mt-[18px]">
                <span className="font-jetbrains text-[9.5px] tracking-[0.14em] text-dim">
                  PARAMETERS
                </span>
                <div className="mt-1">
                  {b.params.map((p) => (
                    <div key={p.n} className="border-b border-line py-2.5">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <code className="font-jetbrains text-[12.5px] font-semibold text-ink">
                          {p.n}
                        </code>
                        <span className="font-jetbrains text-[10px] text-dim">{p.ty}</span>
                        {p.rq && (
                          <span className="rounded-full border border-[color-mix(in_srgb,var(--color-brand-600)_35%,transparent)] bg-[color-mix(in_srgb,var(--color-brand-600)_8%,white)] px-1.5 py-px font-jetbrains text-[8.5px] tracking-[0.06em] text-brand-700">
                            REQUIRED
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-[13px] leading-[1.55] text-muted">{p.d}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {b.rows && (
              <div className="mt-[18px] overflow-hidden rounded-[10px] border border-line-2 bg-line">
                <div className="flex flex-col gap-px">
                  {b.rows.map((r) => (
                    <div
                      key={r.c}
                      className="grid grid-cols-[186px_minmax(0,1fr)] gap-3.5 bg-surface px-3.5 py-[9px] max-[720px]:grid-cols-1"
                    >
                      <code className="font-jetbrains text-[11px] text-ink">{r.c}</code>
                      <span className="text-[12.5px] leading-[1.5] text-muted">{r.d}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {b.foot && (
              <p className="mt-4 font-jetbrains text-[9.5px] leading-[1.8] tracking-[0.1em] text-dim">
                {b.foot}
                {b.footPromise && ` (${roadmapItem(b.footPromise).promised.toUpperCase()})`}
              </p>
            )}
          </div>

          {(b.curl || b.resp) && (
            <div className="sticky top-[84px] flex min-w-0 flex-col gap-3 max-[999px]:static">
              {b.curl && <CodePane src={b.curl} lang="bash" label="REQUEST" copy shadow size="sm" />}
              {b.resp && <CodePane src={b.resp.src} lang={b.resp.lang} label={b.resp.label} size="sm" />}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
